import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAms, Header, Stat, Badge, dt, label } from "./kit";

const LIFECYCLE = [["Procure / register", "in_stock", "A.5.9"], ["Request & approve", "approval", "A.5.15"], ["Assign", "assigned", "A.5.9"],
  ["Activate by QR", "active", "A.7.14"], ["In service", "in_repair", "A.5.24"], ["Loss / theft", "lost", "A.6.8"],
  ["Recover", "pending_return", "A.5.11"], ["Dispose", "disposed", "A.7.14"], ["Evidence", "audit", "A.8.16"]];

export default function AmsDashboard() {
  const { me } = useAms();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/ams/dashboard").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  const c = d.counts;
  const val = { approval: d.pending_approvals, active: d.active, audit: (d.activity || []).length ? "chained" : "—" };
  return (
    <div data-testid="ams-dashboard">
      <Header title="Asset Management" sub={`Signed in as ${me.name} · AMS role: ${me.role}. ISO/IEC 27001:2022 Annex A aligned lifecycle, from procurement to evidenced destruction.`} />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <Stat k="Total assets" v={d.total} testid="ams-stat-total" />
        <Stat k="In stock" v={c.in_stock} testid="ams-stat-in-stock" />
        <Stat k="Assigned" v={c.assigned} hint={`${d.active} activated by custodian`} testid="ams-stat-assigned" />
        <Stat k="Pending approvals" v={d.pending_approvals} testid="ams-stat-approvals" />
        <Stat k="Open workflows" v={d.open_workflows} testid="ams-stat-workflows" />
        <Stat k="Compliance alerts" v={d.alert_count ?? "—"} testid="ams-stat-alerts" />
      </div>
      <div className="ams-card p-5 mb-6 ams-rise">
        <div className="ams-kicker mb-3">End-to-end lifecycle</div>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          {LIFECYCLE.map(([n, k, iso], i) => (
            <div key={k} className="border border-zinc-200 rounded-sm p-3" data-testid={`ams-stage-${k}`}>
              <div className="text-[10px] text-zinc-400">{String(i + 1).padStart(2, "0")} · {iso}</div>
              <div className="text-xs font-medium mt-1 leading-tight">{n}</div>
              <div className="text-lg font-semibold mt-1 tabular-nums">{val[k] ?? c[k] ?? 0}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="ams-card p-5 lg:col-span-2">
          <div className="ams-kicker mb-3">Activity feed</div>
          {!(d.activity || []).length && <div className="text-sm text-zinc-400">No activity yet.</div>}
          {(d.activity || []).map((a) => (
            <div key={a.id} className="flex items-start justify-between gap-3 py-2 border-b border-zinc-100 last:border-0 text-sm">
              <div><span className="font-mono text-[11px] text-blue-700">{a.action}</span> <span className="font-medium">{a.entity_label}</span><div className="text-[11px] text-zinc-500">{a.actor} · {a.control}</div></div>
              <div className="text-[11px] text-zinc-400 whitespace-nowrap">{dt(a.created_at)}</div>
            </div>
          ))}
        </div>
        <div className="space-y-4">
          {d.alerts && <div className="ams-card p-5"><div className="flex justify-between"><div className="ams-kicker mb-3">Open control gaps</div><Link to="/ams/compliance" className="text-xs text-blue-700">View all</Link></div>
            {!d.alerts.length && <div className="text-sm text-emerald-700">No open alerts.</div>}
            {d.alerts.map((a) => <div key={a.id} className="py-1.5 text-sm flex items-start gap-2"><Badge v={a.severity} /><div>{a.title}<div className="text-[11px] text-zinc-500">{a.control} · {a.detail}</div></div></div>)}
          </div>}
          <div className="ams-card p-5" data-testid="ams-my-assets"><div className="ams-kicker mb-3">My assigned kit</div>
            {!d.my_assets.length && <div className="text-sm text-zinc-400">Nothing assigned to you.</div>}
            {d.my_assets.map((a) => <div key={a.id} className="flex items-center justify-between py-1.5 text-sm"><div><div className="font-medium">{a.name}</div><div className="text-[11px] font-mono text-zinc-500">{a.asset_tag}</div></div>
              {a.is_active ? <Badge v="approved" /> : <Link to={`/ams/qr?tag=${a.asset_tag}`} className="text-xs text-blue-700" data-testid={`ams-activate-link-${a.asset_tag}`}>Activate by scan</Link>}</div>)}
            {d.staged_uploads > 0 && <div className="text-[11px] text-amber-700 mt-2">{d.staged_uploads} bulk upload(s) awaiting {label("second-party")} release</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
